// Revocatoria del consentimiento informado (bloque E del 024, SNS-MSP/HCU-form.024): documento propio con los datos de quien revoca
// (paciente o, si no está en capacidad de firmar, su representante legal), fecha y hora, firma manuscrita y PDF firmado en modo `msp`
// (encabezado MSP + el texto del papel). Antes revocar era un botón que creaba un `Consent` inactivo sin datos, sin firma y sin PDF.
// El papel no trae un campo de motivo, así que tampoco se pide.
//
// Wizard: cargando → datos → firma → firma-profesional → listo. Mismo criterio create-only del sistema: el `Consent` original no se
// edita; `construirRevocacion` (lib/consentimientos.ts) crea uno nuevo `inactive` que apunta al original y al PDF firmado.
import type { Consent, DocumentReference, Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams, useSearchParams } from 'react-router';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { codigosFormularioDeEspecialidad } from '@/lib/catalogoEspecialidades';
import { fechaYHoraEcuador, paresRevocatoria024, TOLERANCIA_FUTURO_MS, type MetodoFirma, type PersonaConsentimiento } from '@/lib/consentimiento024';
import { construirRevocacion, SISTEMA_EXTENSION_REVOCA, SISTEMA_SUBTIPO_CONSENTIMIENTO } from '@/lib/consentimientos';
import { useEspecialidadDelPracticante } from '@/lib/useEspecialidadDelPracticante';
import type { PatientChartOutletContext } from '@/paciente/PatientChartPage';
import { CapturaFirmaPaciente, type FirmaPacienteCapturada } from './CapturaFirmaPaciente';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from './EncabezadoMspCampos';
import { PanelFirmaImpresion } from './PanelFirmaImpresion';
import { subirFirmaComoDocumentReference } from './subirFirmaDataUrl';
import { useDatosEncabezadoMsp } from './useDatosEncabezadoMsp';

/** Ruta y `tipoDocumento` de la revocatoria: el código 024 con sufijo, para que el audit log y el PDF firmado la distingan del consentimiento. */
export const CODIGO_REVOCATORIA_024 = '024-revocatoria';

interface DatosRevocatoria {
  fecha: string;
  hora: string;
  representante?: PersonaConsentimiento;
}

type Estado =
  | { fase: 'cargando' }
  | { fase: 'error'; mensaje: string }
  | { fase: 'datos'; consent: Consent }
  | { fase: 'firma'; consent: Consent; datos: DatosRevocatoria }
  | { fase: 'procesando' }
  | { fase: 'firma-profesional'; consent: Consent; datos: DatosRevocatoria; firmaDocRefId: string; metodo: MetodoFirma }
  | { fase: 'listo' };

function subtipoDe(consent: Consent): string | undefined {
  return consent.category?.flatMap((c) => c.coding ?? []).find((c) => c.system === SISTEMA_SUBTIPO_CONSENTIMIENTO)?.code;
}

/** Hora local de Ecuador (UTC-5, sin horario de verano) → ISO. */
export function instanteDeRevocatoria(fecha: string, hora: string): string {
  return new Date(`${fecha}T${hora}:00-05:00`).toISOString();
}

export function RevocatoriaConsentimiento024(): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id } = useParams() as { id: string };
  const [searchParams] = useSearchParams();
  const consentId = searchParams.get('consent');
  const especialidad = useEspecialidadDelPracticante();
  const subject = useMemo<Reference<Patient>>(() => ({ reference: `Patient/${id}` }), [id]);

  const [estado, setEstado] = useState<Estado>({ fase: 'cargando' });
  // Descartar (pedido explícito de Sue 2026-09-27): SOLO existe en el paso
  // 'datos', antes de continuar a la firma.
  const [descartado, setDescartado] = useState(false);
  // Bloqueo de salida sin firmar/descartar (pedido explícito de Sue,
  // 2026-09-27) — pendiente desde 'firma' (ya se captura una firma real)
  // hasta 'listo'; en 'datos' nada se guardó todavía.
  const reportarPendienteFirma = useOutletContext<PatientChartOutletContext | undefined>()?.reportarPendienteFirma;
  const pendienteFirma = (estado.fase === 'firma' || estado.fase === 'procesando' || estado.fase === 'firma-profesional') && !descartado;
  useEffect(() => {
    reportarPendienteFirma?.(pendienteFirma);
  }, [reportarPendienteFirma, pendienteFirma]);
  useEffect(() => () => reportarPendienteFirma?.(false), [reportarPendienteFirma]);
  const [camposEncabezado, setCamposEncabezado] = useState(() => valoresInicialesEncabezadoMsp());
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, camposEncabezado, '024', t('consentimiento.revocatoria.titulo'), {
    conIdentificacionPaciente: true,
    // El bloque E del papel (revocatoria) solo trae firmas: no captura servicio y "Consulta externa" sería un dato inventado.
    sinServicio: true,
  });

  const ahora = useMemo(() => fechaYHoraEcuador(new Date()), []);
  const [fecha, setFecha] = useState(ahora.fecha);
  const [hora, setHora] = useState(ahora.hora);
  const [porRepresentante, setPorRepresentante] = useState(false);
  const [rep, setRep] = useState<PersonaConsentimiento>({ nombre: '', cedula: '', parentesco: '' });
  const [errorDatos, setErrorDatos] = useState<string | null>(null);

  useEffect(() => {
    if (!consentId) {
      setEstado({ fase: 'error', mensaje: t('consentimiento.revocatoria.sinConsentimiento') });
      return;
    }
    let cancelado = false;
    (async () => {
      const consent = await medplum.readResource('Consent', consentId);
      if (consent.patient?.reference !== `Patient/${id}` || subtipoDe(consent) !== 'consent-024') throw new Error(t('consentimiento.revocatoria.noEsDelPaciente'));
      if (consent.status !== 'active') throw new Error(t('consentimiento.revocatoria.noVigente'));
      // El original nunca se edita: si ya hay una revocatoria que apunta a él, no se vuelve a revocar.
      const inactivos = await medplum.searchResources('Consent', { patient: `Patient/${id}`, status: 'inactive', _count: 1000 });
      const yaRevocado = inactivos.some((c) => c.extension?.some((e) => e.url === SISTEMA_EXTENSION_REVOCA && e.valueReference?.reference === `Consent/${consentId}`));
      if (yaRevocado) throw new Error(t('consentimiento.revocatoria.yaRevocado'));
      if (!cancelado) setEstado({ fase: 'datos', consent });
    })().catch((err: unknown) => {
      if (!cancelado) setEstado({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
    });
    return () => {
      cancelado = true;
    };
  }, [medplum, consentId, id, t]);

  function continuarAFirma(consent: Consent): void {
    if (!fecha || !hora) {
      setErrorDatos(t('consentimiento.revocatoria.faltaFechaHora'));
      return;
    }
    const instante = instanteDeRevocatoria(fecha, hora);
    const ms = Date.parse(instante);
    if (Number.isNaN(ms) || ms < Date.parse(consent.dateTime ?? '1970-01-01') || ms > Date.now() + TOLERANCIA_FUTURO_MS) {
      setErrorDatos(t('consentimiento.revocatoria.fechaInvalida'));
      return;
    }
    const representante = porRepresentante ? { nombre: rep.nombre?.trim(), cedula: rep.cedula?.trim(), parentesco: rep.parentesco?.trim() } : undefined;
    if (representante && (!representante.nombre || !representante.cedula || !representante.parentesco)) {
      setErrorDatos(t('consentimiento.error.representanteIncompleto'));
      return;
    }
    setErrorDatos(null);
    setEstado({ fase: 'firma', consent, datos: { fecha, hora, representante } });
  }

  const handleFirma = useCallback(
    async (firma: FirmaPacienteCapturada) => {
      if (estado.fase !== 'firma') return;
      const { consent, datos } = estado;
      setEstado({ fase: 'procesando' });
      try {
        const titulo = datos.representante ? `Firma del representante legal (${firma.metodo})` : `Firma del paciente (${firma.metodo})`;
        const docRef = await subirFirmaComoDocumentReference(medplum, subject, firma.dataUrl, firma.contentType, datos.representante ? 'firma-representante-revocatoria' : 'firma-paciente-revocatoria', titulo);
        setEstado({ fase: 'firma-profesional', consent, datos, firmaDocRefId: docRef.id!, metodo: firma.metodo });
      } catch (err) {
        setEstado({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
      }
    },
    [medplum, subject, estado],
  );

  const handleFirmadoProfesional = useCallback(
    async (docRefProfesional: DocumentReference) => {
      if (estado.fase !== 'firma-profesional') return;
      const { consent, datos, firmaDocRefId } = estado;
      await construirRevocacion(medplum, consent, {
        dateTime: instanteDeRevocatoria(datos.fecha, datos.hora),
        documentReferencePdf: { reference: `DocumentReference/${docRefProfesional.id}` },
        firma: { rol: datos.representante ? 'representante' : 'paciente', documentReference: { reference: `DocumentReference/${firmaDocRefId}` } },
        representante: datos.representante,
      });
      setEstado({ fase: 'listo' });
    },
    [medplum, estado],
  );

  if (especialidad.cargando) return <p className="text-sm text-hce-muted">{t('formulario.cargando')}</p>;
  // Fail-closed, igual que MotorFormularioConsentimiento: sin especialidad resuelta o sin el 024 habilitado, no se revoca desde acá.
  if (!especialidad.codigo || !codigosFormularioDeEspecialidad(especialidad.codigo).includes('024')) {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.fueraDeEspecialidadTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.fueraDeEspecialidadDetalle')}</AlertDescription>
      </Alert>
    );
  }
  if (estado.fase === 'cargando') return <p className="text-sm text-hce-muted">{t('formulario.cargando')}</p>;
  if (estado.fase === 'error') {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.errorTitulo')}</AlertTitle>
        <AlertDescription>{estado.mensaje}</AlertDescription>
      </Alert>
    );
  }

  if (descartado) {
    return (
      <Alert>
        <AlertTitle>{t('formulario.descartadoTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.descartadoDetalle')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-medium text-hce-ink">{t('consentimiento.revocatoria.titulo')}</h3>
        {estado.fase === 'datos' && (
          <Button type="button" variant="outline" size="sm" onClick={() => setDescartado(true)}>
            {t('formulario.descartar')}
          </Button>
        )}
      </div>

      {(estado.fase === 'datos' || estado.fase === 'firma-profesional') && <EncabezadoMspCampos subject={subject} value={camposEncabezado} onChange={setCamposEncabezado} />}

      {estado.fase === 'datos' && (
        <div className="flex max-w-xl flex-col gap-3">
          <p className="text-sm text-hce-muted">{t('consentimiento.revocatoria.instruccion')}</p>
          {errorDatos && (
            <Alert variant="red">
              <AlertDescription>{errorDatos}</AlertDescription>
            </Alert>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="revocatoria-fecha">{t('consentimiento.revocatoria.fecha')}</Label>
              <Input id="revocatoria-fecha" type="date" max={ahora.fecha} value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="revocatoria-hora">{t('consentimiento.revocatoria.hora')}</Label>
              <Input id="revocatoria-hora" type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="revocatoria-representante" checked={porRepresentante} onCheckedChange={(v) => setPorRepresentante(v === true)} />
            <Label htmlFor="revocatoria-representante" className="text-sm text-hce-ink">
              {t('consentimiento.revocatoria.porRepresentante')}
            </Label>
          </div>
          {porRepresentante && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="flex flex-col gap-1">
                <Label htmlFor="revocatoria-rep-nombre">{t('consentimiento.revocatoria.representanteNombre')} *</Label>
                <Input id="revocatoria-rep-nombre" required value={rep.nombre ?? ''} onChange={(e) => setRep({ ...rep, nombre: e.target.value })} />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="revocatoria-rep-cedula">{t('consentimiento.revocatoria.representanteCedula')} *</Label>
                <Input id="revocatoria-rep-cedula" required value={rep.cedula ?? ''} onChange={(e) => setRep({ ...rep, cedula: e.target.value })} />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="revocatoria-rep-parentesco">{t('consentimiento.revocatoria.representanteParentesco')} *</Label>
                <Input id="revocatoria-rep-parentesco" required value={rep.parentesco ?? ''} onChange={(e) => setRep({ ...rep, parentesco: e.target.value })} />
              </div>
            </div>
          )}
          <Button type="button" className="w-fit" onClick={() => continuarAFirma(estado.consent)}>
            {t('consentimiento.continuar')}
          </Button>
        </div>
      )}

      {estado.fase === 'firma' && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-hce-muted">{t(estado.datos.representante ? 'consentimiento.instruccionFirmaRepresentante' : 'consentimiento.revocatoria.instruccionFirma')}</p>
          <CapturaFirmaPaciente onCapturada={(firma) => void handleFirma(firma)} />
        </div>
      )}

      {estado.fase === 'procesando' && <p className="text-sm text-hce-muted">{t('consentimiento.subiendoFirmaPaciente')}</p>}

      {estado.fase === 'firma-profesional' && (
        <div className="flex max-w-md flex-col gap-2">
          <p className="text-sm text-hce-muted">{t('consentimiento.instruccionFirmaProfesional')}</p>
          <PanelFirmaImpresion
            documentoId={`Consent/${estado.consent.id}`}
            tipoDocumento={CODIGO_REVOCATORIA_024}
            titulo={t('consentimiento.revocatoria.titulo')}
            subject={subject}
            especificacionBuilder={async () => ({
              modo: 'msp',
              pares: paresRevocatoria024({
                paciente: { nombre: datosEncabezadoMsp.pacienteNombre, cedula: datosEncabezadoMsp.pacienteIdentificacion },
                fechaConsentimientoISO: estado.consent.dateTime ?? new Date().toISOString(),
                fecha: estado.datos.fecha,
                hora: estado.datos.hora,
                representante: estado.datos.representante,
                metodoFirma: estado.metodo,
              }),
              datosEncabezadoMsp,
            })}
            onFirmado={handleFirmadoProfesional}
          />
        </div>
      )}

      {estado.fase === 'listo' && (
        <Alert>
          <AlertTitle>{t('consentimiento.revocatoria.listoTitulo')}</AlertTitle>
          <AlertDescription>{t('consentimiento.revocatoria.listoDetalle')}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
